/**
 * useSunoTrackActions — общий фундамент для всех UI-элементов, которые запускают
 * действия над существующим Suno-треком (WAV, стемы, MIDI, cover, видеоклип,
 * timestamped lyrics, персона).
 *
 * Используется в SunoActionsSheet — едином hub'е действий над треком,
 * открывающемся из CardModal по кнопке «Работать с треком».
 *
 * Гарантирует одинаковый UX: одна операция за раз, toast-нотификации,
 * цены из `Model.variants` по ключу операции.
 */
import type { MusicProvider } from '../utils/trackOperations'
import { useCallback, useMemo, useState } from 'react'
import { showToast } from '../../../components/Toast'
import { useSunoPolling, type SunoPollingStatus } from './useSunoPolling'
import { useProviders } from '../../../hooks/useProviders'
import type { APIVariant, BrandModel } from '../../../hooks/useProviders'
import type { TaskStatusResponse } from '../../../api/images'
import { translateSunoError } from '../utils/sunoErrors'
import {
	convertToWav,
	stemSeparation,
	generateMidi,
	generateCover,
	createMusicVideo,
	getTimestampedLyrics,
	generatePersona,
} from '../../../api/music'

export type SunoActionKey =
	| 'wav'
	| 'stems'
	| 'stem_minus'
	| 'midi'
	| 'cover'
	| 'video'
	| 'timestamped'
	| 'persona'

export interface SunoActionDef {
	key: SunoActionKey
	label: string
	description: string
	/** Ключ в Model.variants — определяет цену. */
	variantKey: string
	starter: (trackId: string) => Promise<{ ok: boolean; request_id?: string; error?: string }>
}

export const SUNO_TRACK_ACTIONS: SunoActionDef[] = [
	{
		key: 'wav',
		label: 'WAV',
		description: 'Конвертировать в WAV',
		variantKey: 'wav_convert',
		starter: (track_id) => convertToWav({ track_id }),
	},
	{
		key: 'stems',
		label: 'Дорожки',
		description: 'Разделить по инструментам',
		variantKey: 'stem_separation',
		starter: (track_id) => stemSeparation({ track_id, stem_kind: 'stems' }),
	},
	{
		key: 'stem_minus',
		label: 'Минусовка',
		description: 'Вокал и минус двумя файлами',
		variantKey: 'stem_minus',
		starter: (track_id) => stemSeparation({ track_id, stem_kind: 'minus' }),
	},
	{
		key: 'midi',
		label: 'MIDI',
		description: 'Получить MIDI-файл',
		variantKey: 'midi',
		starter: (track_id) => generateMidi({ track_id }),
	},
	{
		key: 'cover',
		label: 'Обложка',
		// По гайду §12 `/music/cover` генерит именно ОБЛОЖКУ (картинку),
		// не cover-версию трека. Cover-версия — это upload-cover (см.
		// параметрическую операцию audioOperation='cover').
		description: 'Сгенерировать обложку (картинку) для трека',
		variantKey: 'cover',
		starter: (track_id) => generateCover({ track_id }),
	},
	{
		key: 'video',
		label: 'Видеоклип',
		description: 'Создать музыкальный видеоклип',
		variantKey: 'music_video',
		starter: (track_id) => createMusicVideo({ track_id }),
	},
	{
		key: 'timestamped',
		label: 'Lyrics+',
		description: 'Текст с таймкодами',
		variantKey: 'timestamped_lyrics',
		starter: (track_id) => getTimestampedLyrics({ track_id }),
	},
	{
		key: 'persona',
		label: 'Персона',
		description: 'Создать персону из голоса трека',
		variantKey: 'persona',
		starter: (track_id) => generatePersona({ track_id }),
	},
]

interface UseSunoTrackActionsResult {
	actions: SunoActionDef[]
	activeKey: SunoActionKey | null
	isRunning: boolean
	status: SunoPollingStatus
	runAction: (action: SunoActionDef, trackId: string) => Promise<void>
	/** Цена действия по variantKey для модели ИМЕННО этого трека (= списанию бэка):
	 *  вариант модели → базовая цена модели → (резерв) min по версиям провайдера. */
	priceOf: (variantKey: string) => number | null
	/** Есть ли у модели отдельный тариф под операцию. Нужно там, где бэк сам
	 *  откатывается на другой ключ (минусовка → цена полного разбора). */
	hasPriceVariant: (variantKey: string) => boolean
}

/**
 * Собирает варианты всех Suno-audio-моделей — для lookup цен.
 * Версии Suno обычно имеют одинаковые цены операций; если разойдутся —
 * показываем минимум (оптимистично для юзера, финальное списание — бэк).
 */
function findPrice(variantsByModel: APIVariant[][], variantKey: string): number | null {
	let min: number | null = null
	for (const variants of variantsByModel) {
		const v = variants.find((x) => x.key === variantKey)
		if (v?.credits_price != null) {
			if (min === null || v.credits_price < min) min = v.credits_price
		}
	}
	return min
}

interface Options {
	/** Вызывается при успешном завершении операции (обновить список). */
	onCompleted?: () => void
	/** Вызывается с полным task status для операций с визуальным результатом
	 *  (stems → 2 URL; midi → JSON нот; timestamped-lyrics → JSON таймингов).
	 *  Если не передан — просто toast + onCompleted. */
	onResult?: (action: SunoActionDef, result: TaskStatusResponse) => void
	/** Провайдер трека — цены берём из моделей ИМЕННО этого провайдера (Mureka-трек
	 *  → Mureka-цены из админки). Иначе на Mureka-треке показывали бы Suno-цену, а
	 *  бэк списывал бы Mureka-цену → расхождение. */
	/** Провайдер трека (providerOf). 'elevenlabs' — озвучка: операций над треком у
	 *  неё нет, цены просто не найдутся. */
	provider?: MusicProvider
	/** Модель ИМЕННО этого трека (например 'mureka-8'). Если задана — цену берём из
	 *  variants этой модели → точное показ=списание для пер-версийных операций
	 *  (у Mureka extend разный по версиям: 8 ≠ 7.6). Нет строки под операцию →
	 *  фолбэк на min по провайдеру (как раньше; версии Suno равны). */
	model?: string | null
}

export function useSunoTrackActions({ onCompleted, onResult, provider = 'suno', model }: Options = {}): UseSunoTrackActionsResult {
	const polling = useSunoPolling()
	const [activeKey, setActiveKey] = useState<SunoActionKey | null>(null)
	const { brands } = useProviders()

	// Цены операций — из variants моделей провайдера трека (значения задаёт админка).
	const providerVariants = useMemo<APIVariant[][]>(() => {
		const result: APIVariant[][] = []
		for (const brand of brands) {
			for (const bm of brand.models) {
				const bp = bm.id.toLowerCase().startsWith('mureka') ? 'mureka' : 'suno'
				if (bm.type === 'audio' && bm.variants && bp === provider) {
					result.push(bm.variants)
				}
			}
		}
		return result
	}, [brands, provider])

	// Модель ИМЕННО этого трека — для точной цены, совпадающей со списанием бэка.
	const trackModel = useMemo<BrandModel | null>(() => {
		if (!model) return null
		const target = model.toLowerCase()
		for (const brand of brands) {
			for (const bm of brand.models) {
				if (bm.type === 'audio' && bm.id.toLowerCase() === target) return bm
			}
		}
		return null
	}, [brands, model])

	const priceOf = useCallback(
		(variantKey: string) => {
			// Цена ОБЯЗАНА совпадать со списанием бэка. get_suno_price для
			// операции берёт: (1) вариант ИМЕННО модели трека, иначе (2) базовую
			// цену этой же модели — и НИКОГДА не min по другим версиям.
			// Раньше при отсутствии варианта фолбэк падал на min по провайдеру →
			// для v5-трека показывалась v4.5all-цена, а бэк списывал v5 (баг
			// «отображение цен действий с треком»: цены версий Suno разошлись).
			if (trackModel) {
				const v = trackModel.variants?.find((x) => x.key === variantKey)
				if (v?.credits_price != null) return v.credits_price
				// Нет строки под операцию у этой версии → базовая цена модели
				// (точно как base_setting.credits_price в get_suno_price).
				if (trackModel.creditsPrice != null) return trackModel.creditsPrice
			}
			// Модель трека не нашлась в данных провайдеров (старый/битый model) —
			// последний резерв: min по версиям провайдера.
			return findPrice(providerVariants, variantKey)
		},
		[trackModel, providerVariants],
	)

	/** Заведён ли У ЭТОЙ модели отдельный тариф под операцию.
	 *
	 *  `priceOf` при отсутствии варианта отдаёт базовую цену модели, поэтому по
	 *  нему «есть ли тариф» не определить. Нужно там, где бэк сам откатывается
	 *  на другой ключ: у минусовки может не быть своей строки — тогда списывается
	 *  цена полного разбора, и показать надо её же, а не цену генерации. */
	const hasPriceVariant = useCallback(
		(variantKey: string) =>
			!!trackModel?.variants?.some((x) => x.key === variantKey && x.credits_price != null),
		[trackModel],
	)

	const isRunning = polling.status === 'running'

	const runAction = useCallback(
		async (action: SunoActionDef, trackId: string) => {
			if (isRunning) return
			setActiveKey(action.key)
			showToast(`${action.description} — запущено`, 'info')
			const res = await polling.run(() => action.starter(trackId))
			if (res) {
				showToast(`${action.label}: готово`, 'success')
				onCompleted?.()
				onResult?.(action, res)
			} else if (polling.error) {
				// SunoActionKey совпадает с SunoOperationContext в большинстве случаев
				// ('midi'/'video'/'persona'/'cover'/'wav'/'stems'/'timestamped').
				showToast(
					`${action.label}: ${translateSunoError(polling.error, action.key as never)}`,
					'error',
				)
			}
			setActiveKey(null)
		},
		[isRunning, polling, onCompleted, onResult],
	)

	return {
		actions: SUNO_TRACK_ACTIONS,
		activeKey,
		isRunning,
		status: polling.status,
		runAction,
		priceOf,
		hasPriceVariant,
	}
}
